/**
 * ActivityChart Component
 * Simple bar chart for activity visualization (no external libraries)
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui";

interface ActivityData {
  date: string;
  sessions: number;
  offers: number;
}

interface ActivityChartProps {
  data: ActivityData[];
  isLoading?: boolean;
}

export function ActivityChart({ data, isLoading = false }: ActivityChartProps) {
  const [period, setPeriod] = useState<"7d" | "30d">("7d");

  // Filter data based on period
  const filteredData = period === "7d" ? data.slice(-7) : data;

  // Calculate max for scaling
  const maxValue = Math.max(
    ...filteredData.flatMap((d) => [d.sessions, d.offers]),
    1
  );

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pl-PL", { day: "numeric", month: "short" });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Aktywność</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-gray-400">
            Ładowanie...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Aktywność</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-gray-400">
            Brak danych aktywności
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Aktywność</CardTitle>
        <div className="flex gap-1">
          <button
            onClick={() => setPeriod("7d")}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              period === "7d"
                ? "bg-primary-100 text-primary-700 font-medium"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            7 dni
          </button>
          <button
            onClick={() => setPeriod("30d")}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              period === "30d"
                ? "bg-primary-100 text-primary-700 font-medium"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            30 dni
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Chart */}
        <div className="h-48 flex items-end gap-1">
          {filteredData.map((item, index) => (
            <div
              key={index}
              className="flex-1 flex flex-col items-center gap-1 group"
            >
              {/* Bars */}
              <div className="w-full flex gap-0.5 h-36 items-end">
                {/* Sessions bar */}
                <div
                  className="flex-1 bg-primary-500 rounded-t-sm transition-all hover:bg-primary-600"
                  style={{
                    height: `${(item.sessions / maxValue) * 100}%`,
                    minHeight: item.sessions > 0 ? "4px" : "0",
                  }}
                  title={`Sesje: ${item.sessions}`}
                />
                {/* Offers bar */}
                <div
                  className="flex-1 bg-success-500 rounded-t-sm transition-all hover:bg-success-600"
                  style={{
                    height: `${(item.offers / maxValue) * 100}%`,
                    minHeight: item.offers > 0 ? "4px" : "0",
                  }}
                  title={`Oferty: ${item.offers}`}
                />
              </div>
              {/* Date label - show every nth depending on period */}
              {(period === "7d" || index % 5 === 0 || index === filteredData.length - 1) && (
                <span className="text-xs text-gray-400 truncate w-full text-center">
                  {formatDate(item.date)}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary-500 rounded-sm" />
            <span className="text-gray-600">Sesje</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-success-500 rounded-sm" />
            <span className="text-gray-600">Oferty</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
